import { useCallback, useState } from 'react';

/**
 * «Последний выбор» человека в этом браузере: аккаунт, период, отчёт, формат.
 * Хранилище может быть недоступно (приватное окно, запрет) — тогда просто
 * не запоминаем и отдаём значение по умолчанию.
 */
const PREFIX = 'if:remembered:';

export function readRemembered<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function writeRemembered<T>(key: string, value: T): void {
  try {
    if (value === undefined || value === null) localStorage.removeItem(PREFIX + key);
    else localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Нет хранилища — не запомним.
  }
}

/** useState, который переживает уход со страницы и перезагрузку. */
export function useRemembered<T>(key: string, fallback: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => readRemembered(key, fallback));
  const set = useCallback(
    (next: T) => {
      setValue(next);
      writeRemembered(key, next);
    },
    [key],
  );
  return [value, set];
}

/** Период хранится строками yyyy-MM-dd: Date в JSON теряет тип. */
export type RememberedRange = { from?: string; to?: string };

export const rangeToRemembered = (r: { from?: Date; to?: Date } | undefined): RememberedRange | undefined =>
  r?.from ? { from: toDay(r.from), to: r.to ? toDay(r.to) : undefined } : undefined;

export const rangeFromRemembered = (r: RememberedRange | undefined): { from?: Date; to?: Date } | undefined =>
  r?.from ? { from: fromDay(r.from), to: r.to ? fromDay(r.to) : undefined } : undefined;

const pad = (n: number) => String(n).padStart(2, '0');
const toDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromDay = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
